import React, { ReactElement } from 'react'
import moment from 'moment-timezone'
import { IBadgeProps } from 'transfix-ui/components/Badge'
import { IAutoAppointmentScheduleSources, Maybe } from 'types/graphqlTypes'
import { EmailErrorIcon, EmailScheduledIcon, EmailSentIcon } from 'transfix-icons'
import { formatDate, getTimeAgoShort } from 'utils/formatDatetime'
import { DISPLAY } from 'constants/display'
import { IShipmentProgressStopAppointmentFragment } from '../graphql/ShipmentProgressStopAppointmentFragment'
import { IShipmentProgressStopFragment } from '../graphql/ShipmentProgressStopFragment'

export const IMA_BOT_ID = '1597'

interface ISourceConfig {
  acronym: string
  label: string
}

interface IBadgeConfig extends ISourceConfig {
  color: IBadgeProps['color']
  description: string
  icon: {
    component: ReactElement
    description: string
  }
}

export const SOURCE_MAPPER: Record<IAutoAppointmentScheduleSources, ISourceConfig> = {
  [IAutoAppointmentScheduleSources.quick_request]: {
    acronym: 'QR',
    label: 'Quick Request',
  },
  [IAutoAppointmentScheduleSources.smart_appointment]: {
    acronym: 'SA',
    label: 'Smart Appointment',
  },
}

const MAX_MINUTES_TO_SA_TO_BE_SENT = 15

interface IAutoAppointmentSchedule
  extends Pick<
    NonNullable<IShipmentProgressStopFragment['autoAppointmentSchedule']>,
    'createdBy' | 'lastRequestedAt' | 'source' | 'requestedCount' | 'createdAt'
  > {}

export const isAutoAppointmentScheduleScheduled = (
  autoAppointmentSchedule: Maybe<
    Pick<IAutoAppointmentSchedule, 'createdAt' | 'requestedCount' | 'source'>
  >,
  smartAppointmentEligible: boolean
): boolean => {
  if (!autoAppointmentSchedule) return smartAppointmentEligible

  /*
   * The SA should not take more than 15 minutes to be sent.
   * This condition is added to avoid confusion with old data.
   */
  if (
    moment().diff(moment(autoAppointmentSchedule.createdAt), 'minutes') >
    MAX_MINUTES_TO_SA_TO_BE_SENT
  ) {
    return false
  }

  return (
    !autoAppointmentSchedule.requestedCount &&
    smartAppointmentEligible &&
    autoAppointmentSchedule.source === IAutoAppointmentScheduleSources.smart_appointment
  )
}

const isSchedulerNotResponding = (
  autoAppointmentSchedule: Maybe<IAutoAppointmentSchedule>,
  isFFTurnedOn: boolean
): boolean => {
  if (!autoAppointmentSchedule) return false

  const lastSent = moment.duration(moment().diff(autoAppointmentSchedule.lastRequestedAt))

  if (isFFTurnedOn) return lastSent.asMinutes() > 120

  return autoAppointmentSchedule.requestedCount === 3 && lastSent.asMinutes() > 120
}

const getEmailSchedulingData = (
  autoAppointmentSchedule: Maybe<IAutoAppointmentSchedule>,
  smartAppointmentEligible: boolean,
  appointment: Pick<IShipmentProgressStopAppointmentFragment, 'timeUpdatedBy' | 'status'>,
  isFFTurnedOn: boolean,
  timezone?: string
): IBadgeConfig | null => {
  if (appointment.status === 'confirmed') return null
  if (!autoAppointmentSchedule) return null

  const isScheduled = isAutoAppointmentScheduleScheduled(
    autoAppointmentSchedule,
    smartAppointmentEligible
  )
  const { createdBy, lastRequestedAt, source } = autoAppointmentSchedule || {}
  const { acronym = '', label = '' } = source ? SOURCE_MAPPER[source] : {}
  const formattedLastRequestedAt = lastRequestedAt
    ? formatDate(lastRequestedAt, 'datetimeFormat', timezone)
    : null
  const isSmartAppointment = source === 'smart_appointment'
  const createByLabel =
    createdBy && createdBy.id !== IMA_BOT_ID && !isSmartAppointment
      ? `sent by ${createdBy.fullName}`
      : 'sent'
  const lastRequestedAtTimeAgo = timezone
    ? `at ${formattedLastRequestedAt}`
    : getTimeAgoShort(lastRequestedAt)
  if (isScheduled) {
    return {
      label,
      acronym,
      color: 'orange',
      description: 'scheduled to send',
      icon: {
        component: <EmailScheduledIcon size='large' color='warning1' />,
        description: `${label} scheduled to send`,
      },
    }
  }

  if (!lastRequestedAt && isSmartAppointment) return null

  const schedulerNotResponding = isSchedulerNotResponding(autoAppointmentSchedule, isFFTurnedOn)

  return {
    label,
    acronym,
    color: 'gray',
    description: `${createByLabel} @ ${formattedLastRequestedAt}`,
    icon: {
      component: schedulerNotResponding ? (
        <EmailErrorIcon size='large' color='error1' />
      ) : (
        <EmailSentIcon size='large' color='coolGray5' />
      ),
      description: `${
        schedulerNotResponding ? `Facility not responding ${DISPLAY.empty} ` : ''
      }${label} ${createByLabel} ${lastRequestedAtTimeAgo}`,
    },
  }
}

export default getEmailSchedulingData
